import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserCheck, UserX, Mail, Shield } from "lucide-react";

const STATUS_BADGE = {
  active: "rounded-full bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800",
  pending: "rounded-full bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800",
  rejected: "rounded-full bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800",
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Clients() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [dialog, setDialog] = useState(null); // { action, user }
  const [acting, setActing] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users?status=${filter}`);
      setUsers(data.users || []);
    } catch (e) { console.error("Fetch users error:", e); toast.error("Failed to load users."); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { setLoading(true); fetchUsers(); }, [fetchUsers]);

  const handleAction = async () => {
    if (!dialog) return;
    setActing(true);
    try {
      const endpoint = dialog.action === "approve"
        ? `/admin/users/${dialog.user.user_id}/approve`
        : `/admin/users/${dialog.user.user_id}/reject`;
      await api.post(endpoint);
      toast.success(`${dialog.user.name} has been ${dialog.action === "approve" ? "approved" : "rejected"}.`);
      fetchUsers();
    } catch (e) { console.error("Action error:", e); toast.error("Action failed. Please try again."); }
    finally { setActing(false); setDialog(null); }
  };

  const filtered = users;

  return (
    <div data-testid="admin-clients" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Client Management</h1>
        <p className="mt-1 text-sm text-muted-foreground">Approve, reject, or review all registered users.</p>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger data-testid="filter-all" value="all">All</TabsTrigger>
          <TabsTrigger data-testid="filter-pending" value="pending">Pending</TabsTrigger>
          <TabsTrigger data-testid="filter-active" value="active">Active</TabsTrigger>
          <TabsTrigger data-testid="filter-rejected" value="rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="rounded-xl ring-1 ring-black/5 dark:ring-white/5 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">No users match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="clients-table">
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="font-medium">Name</TableHead>
                  <TableHead className="font-medium">Email</TableHead>
                  <TableHead className="font-medium">Status</TableHead>
                  <TableHead className="font-medium">Provider</TableHead>
                  <TableHead className="font-medium">Account No</TableHead>
                  <TableHead className="font-medium">Joined</TableHead>
                  <TableHead className="font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow key={u.user_id} data-testid={`client-row-${u.user_id}`} className="hover:bg-muted/30 transition-colors duration-150">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {u.name}
                        {u.role === "admin" && <Shield className="size-3.5 text-brand" />}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[u.status] || ""}`}>{(u.status || "unknown").toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        {u.auth_provider === "google" ? (
                          <><svg width="14" height="14" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg> Google</>
                        ) : (
                          <><Mail className="size-3.5" /> Email</>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">{u.account_no || "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{fmtDate(u.created_at)}</TableCell>
                    <TableCell className="text-right">
                      {u.status === "pending" && u.role !== "admin" ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <Button data-testid={`approve-${u.user_id}`} size="sm" variant="outline" className="h-7 px-2.5 text-xs rounded-lg text-emerald-700 border-emerald-200 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-800 dark:hover:bg-emerald-900/20" onClick={() => setDialog({ action: "approve", user: u })}>
                            <UserCheck className="size-3.5 mr-1" /> Approve
                          </Button>
                          <Button data-testid={`reject-${u.user_id}`} size="sm" variant="outline" className="h-7 px-2.5 text-xs rounded-lg text-red-700 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20" onClick={() => setDialog({ action: "reject", user: u })}>
                            <UserX className="size-3.5 mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dialog?.action === "approve" ? "Approve User" : "Reject User"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dialog?.action === "approve"
                ? `Approve ${dialog?.user?.name} (${dialog?.user?.email})? They will be granted portal access and receive a welcome message.`
                : `Reject ${dialog?.user?.name} (${dialog?.user?.email})? They will not be able to access the portal.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dialog-cancel" disabled={acting}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="dialog-confirm" onClick={handleAction} disabled={acting}
              className={dialog?.action === "reject" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : "bg-brand text-white hover:bg-[#0B8E8D]"}>
              {acting ? "Processing…" : dialog?.action === "approve" ? "Approve" : "Reject"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
